/**
 * The post-answer view.
 *
 * Shows the correct answer AND the one-line rule the exercise was about, which
 * the shared back cannot do — a grammar card that is missed should re-teach,
 * not just correct. The rule travels on the card payload rather than being
 * fetched, because the session already loaded it and a round-trip per miss
 * would be a spinner in the middle of a five-minute session.
 */
import { CheckCircle2, XCircle } from 'lucide-react';

import { cn } from '@/lib/utils';

import type { CardBackProps } from '../types';

import { correctAnswerText, type GrammatikPayload } from './types';

export function CardBack({
  card,
  correct,
  user_answer,
  isLastCard,
  onNext,
}: CardBackProps<GrammatikPayload>) {
  const payload = card.payload;
  // When several orders are correct, show the one the user actually built.
  // Printing the canonical answer over a different-but-accepted sentence reads
  // as "right, but not that" — the opposite of what `also_accept` is for.
  const answer = correct && user_answer ? user_answer : correctAnswerText(payload);

  return (
    <div className="border-border bg-card flex flex-col gap-6 rounded-2xl border p-6 shadow-sm sm:p-8">
      <div className="flex items-center gap-3">
        {correct ? (
          <CheckCircle2 className="text-success size-6" aria-hidden />
        ) : (
          <XCircle className="text-destructive size-6" aria-hidden />
        )}
        <span className={cn('font-medium', correct ? 'text-success' : 'text-destructive')}>
          {correct ? 'Correct' : user_answer === null ? 'Skipped' : 'Not quite'}
        </span>
      </div>

      <div className="flex flex-col gap-2">
        <span className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
          Answer
        </span>
        <p className="font-display text-2xl">{answer}</p>
        {!correct && user_answer !== null && (
          <p className="text-muted-foreground text-sm">
            You wrote <span className="line-through">{user_answer}</span>
          </p>
        )}
      </div>

      <button
        type="button"
        onClick={onNext}
        className="bg-primary text-primary-foreground focus-visible:ring-ring min-h-12 w-full rounded-lg px-4 font-medium focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
      >
        {isLastCard ? 'Finish' : 'Next'}
      </button>
    </div>
  );
}
